import {all, call, put, putResolve, select, take, takeLatest} from 'redux-saga/effects';
import {SagaIterator} from 'redux-saga';
import {equals, pick} from 'ramda';
import {
    sendMobileAnalyticsEvent,
    CATEGORY as CATEGORY_ANALYTICS,
} from '@optimaxdev/analytics/mobile';
import {FULFILLED} from 'redux-promise-middleware';

import {getMyFitFilters, isMyFitFiltersFetched} from 'features/myFit/selectors/myFit';
import {
    fetchMyFit,
    MyFitFiltersType,
    toggleMyFitState,
    setMyFitState,
    applyMyFitOnCategory,
} from 'features/myFit/reducers/myFit';
import {CATEGORY, CUSTOMER} from 'constants/actions';
import flags, {isHtoEnabled} from 'constants/flags/flags';

import {ActionType} from 'constants/flow/flowTypes';
import {isCustomerLogged} from 'selectors/customer/customer';
import {
    getCountFilters,
    getIsCategoryItemsFulfilled,
    getCategoryItemsCountIsFulfilled,
} from 'selectors/category/category';
import {isCurrentPageCategory} from 'selectors/app/app';

import {isMyFitInFilters, CATEGORIES_WITH_HARDCODED_FILTERS, CountFiltersType} from './utils';
import {updateFilters, handleFilterChange, clearFilters} from './filters';

/**
 * Apply My Fit filters preset to the filters
 * @param {ActionType} action - action
 */
export function* applyMyFitPreset(action?: ActionType): SagaIterator {
    const [filters, countFilters]: [MyFitFiltersType, CountFiltersType] = yield all([
        select(getMyFitFilters),
        select(getCountFilters),
    ]);

    const isMyFitApplicable = yield call(isMyFitInFilters, filters, countFilters);

    if (
        (CATEGORIES_WITH_HARDCODED_FILTERS[window.location.pathname] &&
            !equals(CATEGORIES_WITH_HARDCODED_FILTERS[window.location.pathname], filters.gender)) ||
        !isMyFitApplicable
    ) {
        yield put(setMyFitState(false));
        return;
    }

    if (action) {
        yield call(window.scrollTo, 0, Math.abs(parseInt(document.body.style.top, 10)));
    }

    yield call(updateFilters, filters);
}

/**
 * Checks if the required requests are fulfilled,
 * and waits for them if not
 */
export function* waitForCategoryData(): SagaIterator {
    if (!(yield select(isMyFitFiltersFetched))) {
        yield take(`${fetchMyFit}_${FULFILLED}`);
    }

    if (!(yield select(getCategoryItemsCountIsFulfilled))) {
        yield take(`${CATEGORY.GET_CATEGORY_ITEMS_COUNT}_${FULFILLED}`);
    }

    if (!(yield select(getIsCategoryItemsFulfilled))) {
        yield take(`${CATEGORY.GET_CATEGORY_ITEMS}_${FULFILLED}`);
    }
}

/**
 * Initial apply My Filters on the category page
 */
export function* applyMyFitOnCTP(): SagaIterator {
    if (
        !flags.myFit.isEnabled() ||
        (yield call(isHtoEnabled)) ||
        !(yield select(isCurrentPageCategory))
    ) {
        return;
    }

    yield call(waitForCategoryData);
    yield putResolve(setMyFitState(true));
    yield call(applyMyFitPreset);
}

/**
 * Check if the filters contain a My Fit preset and update My Fit state
 * @param {MyFitFiltersType} myFitFilters - myFitFilters
 * @param {Object} activeFiltersById - activeFiltersById
 * @param {boolean} isMyFitEnabled - isMyFitEnabled
 */
export function* checkIsMyFitPreset(
    myFitFilters: MyFitFiltersType,
    activeFiltersById: Record<string, number[]>,
    isMyFitEnabled: boolean,
) {
    const isPreset = equals(pick(Object.keys(myFitFilters), activeFiltersById), myFitFilters);

    if (isMyFitEnabled !== isPreset) {
        yield call(
            sendMobileAnalyticsEvent,
            CATEGORY_ANALYTICS.MY_FIT,
            `Filters Section - State ${isPreset ? 'On' : 'Off'} - Changed by filter`,
        );

        yield put(setMyFitState(isPreset));
    }
}

/**
 * Fetch My Fit filters
 */
export function* getMyFit(): SagaIterator {
    if (!flags.myFit.isEnabled()) {
        return;
    }

    const [isMyFitFetched, isLogged] = yield all([
        select(isMyFitFiltersFetched),
        select(isCustomerLogged),
    ]);

    if (!isMyFitFetched && isLogged) {
        yield all([put(fetchMyFit()), call(applyMyFitOnCTP)]);
    }
}

/**
 * Start My Fit Saga
 */
export function* myFitSaga() {
    yield takeLatest(`${CUSTOMER.GET_CUSTOMER_DATA}_${FULFILLED}`, getMyFit);
    yield takeLatest(`${toggleMyFitState}`, applyMyFitPreset);
    yield takeLatest(`${applyMyFitOnCategory}`, applyMyFitOnCTP);
    yield takeLatest(CATEGORY.CHANGED_FILTER, handleFilterChange);
    yield takeLatest(CATEGORY.REMOVE_ACTIVE_FILTERS, clearFilters);
}
